import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { DataService } from './data.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../environments/environment';

/**
 *  PUSH-ÉRTESÍTÉS – az eszköz regisztrálása és a megnyitott értesítés kezelése.
 *
 *  A kiszolgáló (push-ertesito.js + push-kuldo.js) feljegyzésről és üzenetről küld értesítést a
 *  tanulónak és a családjának, közvetlenül a Google-nek (Android, FCM) és az Apple-nek (iOS, APNs).
 *  2026-10-15-ig OneSignalon ment; a user döntésére kivettük. Ehhez tudnia kell, melyik eszköz
 *  kié: az app minden indításkor elküldi a készülék push-tokenjét a platformmal, a bundle id-vel
 *  és a belépett felhasználó nevével (`/api/pushstore`). Minden indításkor, nem csak az első
 *  belépéskor – így a fiókváltás, a bekötött név és a cserélődött token is helyre kerül.
 *
 *  Csak a SULINFORM-appban: az értesítések iskolai eseményekről szólnak, és a többi alrendszer-
 *  appnak nincs Firebase-kliense (Androidon a regisztráció ott el sem indulhatna).
 *
 *  Az értesítésre koppintva az app a hozzá tartozó képernyőt nyitja (`data.route`). Nyitott
 *  appnál Androidon a rendszer nem mutatja az értesítést, ezért ott egy rövid jelzés jelenik meg.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private initialized = false;
  private token = '';
  private appId = '';
  private elkuldve = '';

  constructor(private http: HttpClient, private data: DataService, public oauth: OAuthService,
              private router: Router, private zone: NgZone) {}

  private get tamogatott(): boolean {
    const mod = String((environment as any).fixedMode || 'SULINFORM').toUpperCase();
    return Capacitor.isNativePlatform() && mod === 'SULINFORM';
  }

  private async regisztral(): Promise<void> {
    try {
      if (!this.token) return;
      const user$ = this.data.belepoKilet(this.oauth.getIdentityClaims() || {}) as any;
      const user = String(user$?.username || '').trim();
      if (!user || this.elkuldve === `${this.token}|${user}`) return;
      await firstValueFrom(this.http.post('/api/pushstore', {
        token: this.token, user, platform: Capacitor.getPlatform(), app_id: this.appId,
      }));
      this.elkuldve = `${this.token}|${user}`;
    } catch (e) {
      console.warn('[push] az eszköz regisztrálása nem sikerült', e);
    }
  }

  async init(): Promise<void> {
    if (!this.tamogatott) return;
    if (this.initialized) { await this.regisztral(); return; }
    this.initialized = true;

    try {
      await this.oauth.loadUserProfile().catch(() => null);
      this.appId = (await App.getInfo().catch(() => null))?.id || '';

      await PushNotifications.addListener('registration', (t) => {
        this.token = String(t?.value || '').trim();
        this.regisztral();
      });
      await PushNotifications.addListener('registrationError', (e) => console.warn('[push] regisztrációs hiba', e));
      await PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
        const route = String(a?.notification?.data?.route || '').trim();
        if (route.startsWith('/')) this.zone.run(() => this.router.navigateByUrl(route));
      });
      if (Capacitor.getPlatform() === 'android') {
        //  A kiszolgáló ezzel a csatornával küld (push-kuldo.js ANDROID_CSATORNA); a felhasználó a
        //  rendszerbeállításban külön állíthatja a hangját.
        await PushNotifications.createChannel({ id: 'ertesitesek', name: 'Értesítések', importance: 4, visibility: 0 })
          .catch(() => null);
        await PushNotifications.addListener('pushNotificationReceived', (n) => {
          const szoveg = [n?.title, n?.body].filter(Boolean).join(' – ');
          if (szoveg) this.zone.run(() => this.data.sendToast(szoveg, 'info'));
        });
      }

      let engedely = await PushNotifications.checkPermissions();
      if (engedely.receive === 'prompt' || engedely.receive === 'prompt-with-rationale') {
        engedely = await PushNotifications.requestPermissions();
      }
      if (engedely.receive === 'granted') await PushNotifications.register();
    } catch (e) {
      console.warn('[push] az értesítések bekapcsolása nem sikerült', e);
    }
  }
}
